import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import { ApiError, makeClient } from '../../api/client'
import { useAuth } from '../../auth/AuthContext'

// --- API types (mirror backend admin_uploads.go shapes) --------------------

type UploadStatus = 'previewed' | 'committing' | 'committed' | 'failed' | 'archived'

type UploadSummary = {
  id: string
  file_name: string
  file_size: number
  file_kind: 'csv' | 'xlsx'
  data_kind: string
  status: UploadStatus
  total_rows?: number | null
  accepted_rows: number
  rejected_rows: number
  duplicate_rows: number
  headers: string[]
  suggested_map: Record<string, string>
  created_at: string
  committed_at?: string | null
}

type UploadDetail = UploadSummary & {
  sample_rows: Array<Record<string, string>>
  mapping?: Record<string, string>
  errors: Array<{ row: number; code: string; message: string }>
  target_fields: { required: string[]; optional: string[] }
}

const UPLOADS_KEY = ['admin', 'uploads'] as const

const DATA_KIND_LABELS: Record<string, string> = {
  jobs: 'Jobs',
}

export default function UploadsPanel() {
  const { accessToken } = useAuth()
  const client = useMemo(() => makeClient(() => accessToken), [accessToken])
  const qc = useQueryClient()

  const uploadsQ = useQuery({
    queryKey: UPLOADS_KEY,
    queryFn: async () => {
      const { data } = await client.callEnvelope<UploadSummary[]>('/api/v1/admin/uploads')
      return data ?? []
    },
    refetchInterval: (query) => {
      // If any upload is still processing, poll for updates.
      const uploads = (query.state.data ?? []) as UploadSummary[]
      const inFlight = uploads.some((u) => u.status === 'committing' || u.status === 'previewed')
      return inFlight ? 3000 : false
    },
  })

  const [uploading, setUploading] = useState(false)
  const [viewing, setViewing] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<UploadSummary | null>(null)

  const invalidate = () => qc.invalidateQueries({ queryKey: UPLOADS_KEY })

  return (
    <>
      <Paper sx={{ p: 2, mt: 2 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>File uploads</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Upload a CSV or Excel export from your WFM to populate the Control Tower without wiring a live webhook.
              Ideal for one-off imports, historical backfills, and pre-live tenant setup.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<CloudUploadOutlinedIcon />}
            onClick={() => setUploading(true)}
          >
            Upload file
          </Button>
        </Stack>

        {uploadsQ.isLoading && <Typography variant="body2">Loading…</Typography>}
        {uploadsQ.isError && (
          <Alert severity="error">
            {uploadsQ.error instanceof Error ? uploadsQ.error.message : 'Failed to load uploads.'}
          </Alert>
        )}

        {uploadsQ.data && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Rows</TableCell>
                  <TableCell align="right">Accepted</TableCell>
                  <TableCell align="right">Rejected</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uploadsQ.data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
                      No uploads yet. Click <b>Upload file</b> to bring in your first CSV or Excel export.
                    </TableCell>
                  </TableRow>
                )}
                {uploadsQ.data.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.file_name}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {formatBytes(u.file_size)} · {u.file_kind.toUpperCase()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={DATA_KIND_LABELS[u.data_kind] ?? u.data_kind} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{formatWhen(u.created_at)}</TableCell>
                    <TableCell><StatusChip status={u.status} /></TableCell>
                    <TableCell align="right">{u.total_rows ?? '—'}</TableCell>
                    <TableCell align="right">{u.accepted_rows}</TableCell>
                    <TableCell align="right">{u.rejected_rows}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => setViewing(u.id)}>
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setDeleting(u)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {uploading && (
        <UploadWizard
          open
          client={client}
          getToken={() => accessToken}
          onClose={() => setUploading(false)}
          onCommitted={() => {
            setUploading(false)
            invalidate()
            // Uploaded jobs land in fact_job (+ job_created events fan
            // out on the bus so rules can raise exceptions). Refresh
            // every downstream key so the operator sees the effect
            // without hand-refreshing each page.
            //
            // Broad invalidation is cheap: TanStack Query only refetches
            // the ones the user's viewing (or that come back into view).
            const downstreamKeys = [
              'jobs',
              'engineers',
              'exceptions',
              'exception',
              'exception-history',
              'enterprise',
              'analytics',
              'analytics-ftf',
              'analytics-trend',
              'control-tower',
              'cost',
              'cost-to-serve',
              'readiness',
              'preflight',
              'asset-health',
              'notifications',
              'notification-events',
              'engineer-workload',
              'engineer-today',
              'live-map',
              'map',
              'stats',
              'reason-codes',
              'audit',
            ]
            downstreamKeys.forEach((key) => qc.invalidateQueries({ queryKey: [key] }))
          }}
        />
      )}
      {viewing && (
        <UploadDetailDialog
          open
          uploadId={viewing}
          client={client}
          onClose={() => setViewing(null)}
        />
      )}
      {deleting && (
        <DeleteUploadDialog
          open
          upload={deleting}
          client={client}
          onClose={() => setDeleting(null)}
          onDone={() => { setDeleting(null); invalidate() }}
        />
      )}
    </>
  )
}

// --- wizard ----------------------------------------------------------------

type WizardProps = {
  open: boolean
  client: ReturnType<typeof makeClient>
  getToken: () => string | null
  onClose: () => void
  onCommitted: () => void
}

function UploadWizard({ open, client, getToken, onClose, onCommitted }: WizardProps) {
  const [dataKind] = useState<'jobs'>('jobs')
  const [file, setFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [detail, setDetail] = useState<UploadDetail | null>(null)
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [uploadPct, setUploadPct] = useState<number | null>(null)
  const [stageError, setStageError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const stageMut = useMutation({
    mutationFn: async () => {
      if (!file) throw new ApiError(0, 'no_file', 'Pick a file to upload.')
      setStageError(null)
      setUploadPct(0)
      return await uploadWithProgress<UploadDetail>({
        url: `/api/v1/admin/uploads?data_kind=${encodeURIComponent(dataKind)}`,
        method: 'POST',
        file,
        token: getToken(),
        onProgress: (pct) => setUploadPct(pct),
      })
    },
    onSuccess: (d) => {
      setDetail(d)
      setMapping({ ...d.suggested_map })
      setUploadPct(null)
    },
    onError: (e) => {
      setUploadPct(null)
      setStageError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Upload failed.')
    },
  })

  const commitMut = useMutation({
    mutationFn: async () => {
      if (!detail) throw new ApiError(0, 'no_detail', 'Nothing staged.')
      return client.call<UploadDetail>(`/api/v1/admin/uploads/${detail.id}/commit`, {
        method: 'POST',
        body: { mapping },
      })
    },
    onSuccess: (d) => setDetail(d),
  })

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files?.[0]
    if (f) setFile(f)
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (f) setFile(f)
  }

  function reset() {
    setFile(null)
    setDetail(null)
    setMapping({})
    setUploadPct(null)
    setStageError(null)
    commitMut.reset()
    stageMut.reset()
  }

  const step: 'pick' | 'map' | 'result' =
    commitMut.isSuccess || (detail && (detail.status === 'committed' || detail.status === 'failed'))
      ? 'result'
      : detail
      ? 'map'
      : 'pick'

  const requiredTargets = detail?.target_fields.required ?? []
  const optionalTargets = detail?.target_fields.optional ?? []
  const allTargets = ['', ...requiredTargets, ...optionalTargets]
  const targetedNow = new Set(Object.values(mapping).filter(Boolean))
  const missingRequired = requiredTargets.filter((t) => !targetedNow.has(t))

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Upload file
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
          {step === 'pick' && 'Select a CSV or Excel file to import.'}
          {step === 'map' && 'Confirm how your columns map to the target fields, then commit.'}
          {step === 'result' && 'Import complete.'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {step === 'pick' && (
          <Stack spacing={2}>
            <Box
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: '2px dashed',
                borderColor: dragOver ? 'secondary.main' : 'divider',
                borderRadius: 2,
                p: 6,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: dragOver ? 'action.hover' : 'transparent',
                transition: 'all 120ms ease',
              }}
            >
              <FileUploadOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {file ? file.name : 'Drop a CSV or Excel file here'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {file ? `${formatBytes(file.size)} · ${file.type || 'unknown type'}` : 'or click to choose · max 25 MB'}
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.tsv,.txt,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                hidden
                onChange={handleFileChange}
              />
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Target data</Typography>
              <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                <Select value={dataKind}>
                  <MenuItem value="jobs">Jobs — work orders (populates Control Tower + Enterprise)</MenuItem>
                  <MenuItem value="engineers" disabled>Engineers — field workforce (coming soon)</MenuItem>
                  <MenuItem value="premises" disabled>Premises — site catalogue (coming soon)</MenuItem>
                  <MenuItem value="job_events" disabled>Job events — status timeline (coming soon)</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                Jobs is the fastest way to populate every downstream page — engineers and premises get lazily provisioned from each row's engineer_ref, so a single jobs upload lights up Control Tower, Enterprise, Cost-to-Serve, Engineer Workload, and Exception Analytics at once.
              </Typography>
            </Box>

            {uploadPct !== null && (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Uploading… {uploadPct}%
                </Typography>
                <LinearProgress variant="determinate" value={uploadPct} sx={{ mt: 0.5 }} />
              </Box>
            )}

            {stageError && <Alert severity="error">{stageError}</Alert>}
          </Stack>
        )}

        {step === 'map' && detail && (
          <Stack spacing={2}>
            <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />}>
              We&rsquo;ve pre-mapped {Object.keys(detail.suggested_map).length} of {detail.headers.length} columns.
              Review the mapping below — required fields are marked in red.
            </Alert>

            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 180 }}>Source column</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Target field</TableCell>
                    <TableCell>Sample values</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.headers.map((h) => {
                    const currentTarget = mapping[h] ?? ''
                    const samples = detail.sample_rows.slice(0, 3).map((r) => r[h]).filter(Boolean)
                    const isRequired = requiredTargets.includes(currentTarget)
                    return (
                      <TableRow key={h}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{h}</Typography>
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={currentTarget}
                              onChange={(e) => {
                                const v = e.target.value
                                setMapping((m) => {
                                  const next = { ...m }
                                  if (v) next[h] = v
                                  else delete next[h]
                                  return next
                                })
                              }}
                              displayEmpty
                              renderValue={(v) => {
                                if (!v) return <Typography variant="caption" sx={{ color: 'text.secondary' }}>— skip —</Typography>
                                const req = requiredTargets.includes(v as string)
                                return (
                                  <Typography variant="body2" sx={{ color: req ? 'error.main' : 'text.primary', fontWeight: req ? 600 : 400 }}>
                                    {v}{req ? ' *' : ''}
                                  </Typography>
                                )
                              }}
                            >
                              {allTargets.map((t) => (
                                <MenuItem key={t || '__skip'} value={t}>
                                  {t
                                    ? `${t}${requiredTargets.includes(t) ? ' * (required)' : ''}`
                                    : '— skip this column —'}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {isRequired && (
                            <Typography variant="caption" sx={{ color: 'error.main' }}>required</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                            {samples.length > 0 ? samples.join(' · ') : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {missingRequired.length > 0 && (
              <Alert severity="warning">
                Still missing: <b>{missingRequired.join(', ')}</b>. Map every required field before committing.
              </Alert>
            )}

            {commitMut.isPending && <LinearProgress />}
            {commitMut.error && (
              <Alert severity="error">
                {commitMut.error instanceof ApiError ? commitMut.error.message : 'Commit failed.'}
              </Alert>
            )}
          </Stack>
        )}

        {step === 'result' && detail && (
          <Stack spacing={2}>
            <Alert
              severity={detail.status === 'committed' ? 'success' : 'error'}
              icon={detail.status === 'committed' ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
            >
              {detail.status === 'committed'
                ? `Committed ${detail.accepted_rows.toLocaleString()} of ${detail.total_rows ?? detail.accepted_rows} rows.`
                : `Commit failed — no rows accepted.`}
            </Alert>
            <Stack direction="row" spacing={3}>
              <Metric label="Accepted" value={detail.accepted_rows} color="success.main" />
              <Metric label="Rejected" value={detail.rejected_rows} color="error.main" />
              <Metric label="Duplicates updated" value={detail.duplicate_rows} color="warning.main" />
              <Metric label="Total rows" value={detail.total_rows ?? 0} color="text.primary" />
            </Stack>
            {detail.errors.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Row errors ({detail.errors.length}{detail.errors.length >= 200 ? '+' : ''})
                </Typography>
                <TableContainer sx={{ maxHeight: 240 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Code</TableCell>
                        <TableCell>Message</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.errors.slice(0, 100).map((e, i) => (
                        <TableRow key={i}>
                          <TableCell>{e.row}</TableCell>
                          <TableCell><Chip label={e.code} size="small" variant="outlined" /></TableCell>
                          <TableCell><Typography variant="caption">{e.message}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'pick' && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              color="secondary"
              disabled={!file || stageMut.isPending}
              onClick={() => stageMut.mutate()}
            >
              {stageMut.isPending ? 'Uploading…' : 'Upload & preview'}
            </Button>
          </>
        )}
        {step === 'map' && (
          <>
            <Button onClick={reset}>Choose different file</Button>
            <Button
              variant="contained"
              color="secondary"
              disabled={missingRequired.length > 0 || commitMut.isPending}
              onClick={() => commitMut.mutate()}
            >
              {commitMut.isPending ? 'Committing…' : `Commit ${detail?.total_rows ?? ''} rows`.trim()}
            </Button>
          </>
        )}
        {step === 'result' && (
          <>
            <Button onClick={reset}>Upload another</Button>
            <Button variant="contained" color="secondary" onClick={onCommitted}>Done</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

// --- detail dialog (read-only) --------------------------------------------

function UploadDetailDialog({ uploadId, client, onClose }: {
  open: boolean
  uploadId: string
  client: ReturnType<typeof makeClient>
  onClose: () => void
}) {
  const detailQ = useQuery({
    queryKey: ['admin', 'uploads', uploadId],
    queryFn: async () => {
      const { data } = await client.callEnvelope<UploadDetail>(`/api/v1/admin/uploads/${uploadId}`)
      return data
    },
  })

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload detail</DialogTitle>
      <DialogContent dividers>
        {detailQ.isLoading && <LinearProgress />}
        {detailQ.isError && (
          <Alert severity="error">
            {detailQ.error instanceof Error ? detailQ.error.message : 'Failed to load upload.'}
          </Alert>
        )}
        {detailQ.data && <UploadDetailView detail={detailQ.data} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

function UploadDetailView({ detail }: { detail: UploadDetail }) {
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle2">{detail.file_name}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {formatBytes(detail.file_size)} · {detail.file_kind.toUpperCase()} · uploaded {formatWhen(detail.created_at)}
          {detail.committed_at && ` · committed ${formatWhen(detail.committed_at)}`}
        </Typography>
      </Box>
      <Stack direction="row" spacing={3}>
        <Metric label="Status" value={detail.status} color="text.primary" />
        <Metric label="Accepted" value={detail.accepted_rows} color="success.main" />
        <Metric label="Rejected" value={detail.rejected_rows} color="error.main" />
        <Metric label="Duplicates updated" value={detail.duplicate_rows} color="warning.main" />
      </Stack>
      {detail.mapping && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Column mapping used</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Source</TableCell>
                  <TableCell>Target</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(detail.mapping).map(([src, tgt]) => (
                  <TableRow key={src}>
                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{src}</Typography></TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{tgt}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      {detail.errors.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Errors ({detail.errors.length})</Typography>
          <TableContainer sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.errors.map((e, i) => (
                  <TableRow key={i}>
                    <TableCell>{e.row}</TableCell>
                    <TableCell><Chip label={e.code} size="small" variant="outlined" /></TableCell>
                    <TableCell><Typography variant="caption">{e.message}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Stack>
  )
}

// --- delete confirm --------------------------------------------------------

function DeleteUploadDialog({ upload, client, onClose, onDone }: {
  open: boolean
  upload: UploadSummary
  client: ReturnType<typeof makeClient>
  onClose: () => void
  onDone: () => void
}) {
  const mut = useMutation({
    mutationFn: () => client.call(`/api/v1/admin/uploads/${upload.id}`, { method: 'DELETE' }),
    onSuccess: onDone,
  })
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Remove upload record</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Delete the record of <b>{upload.file_name}</b>? Rows already committed to fact_job stay in place —
          only the upload history entry is removed.
        </DialogContentText>
        {mut.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {mut.error instanceof ApiError ? mut.error.message : 'Delete failed.'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" disabled={mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Removing…' : 'Remove'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// --- helpers ---------------------------------------------------------------

function StatusChip({ status }: { status: UploadStatus }) {
  const map: Record<UploadStatus, { color: 'default' | 'success' | 'warning' | 'error' | 'info'; icon?: React.ReactNode; label: string }> = {
    previewed:  { color: 'info',    icon: <HourglassEmptyIcon fontSize="inherit" />, label: 'Awaiting commit' },
    committing: { color: 'warning', icon: <HourglassEmptyIcon fontSize="inherit" />, label: 'Committing…' },
    committed:  { color: 'success', icon: <CheckCircleOutlineIcon fontSize="inherit" />, label: 'Committed' },
    failed:     { color: 'error',   icon: <ErrorOutlineIcon fontSize="inherit" />, label: 'Failed' },
    archived:   { color: 'default', label: 'Archived' },
  }
  const cfg = map[status]
  return <Chip icon={cfg.icon as React.ReactElement | undefined} label={cfg.label} color={cfg.color} size="small" variant="outlined" />
}

function Metric({ label, value, color }: { label: string; value: number | string; color: string }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color }}>{typeof value === 'number' ? value.toLocaleString() : value}</Typography>
    </Box>
  )
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

function formatWhen(iso?: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
  } catch {
    return '—'
  }
}

// XHR-based upload so we can report a real progress percentage (fetch has no
// upload progress). Envelope-aware error surfacing.
async function uploadWithProgress<T>({
  url,
  method,
  file,
  token,
  onProgress,
}: {
  url: string
  method: string
  file: File
  token: string | null
  onProgress: (pct: number) => void
}): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(method, url)
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.setRequestHeader('Accept', 'application/json')
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
    }
    xhr.onload = () => {
      let payload: { data?: T; errors?: Array<{ code: string; message: string }> } | null = null
      try { payload = JSON.parse(xhr.responseText) } catch { /* non-JSON */ }
      if (xhr.status >= 200 && xhr.status < 300 && payload?.data) {
        resolve(payload.data)
      } else {
        const first = payload?.errors?.[0]
        reject(new ApiError(xhr.status, first?.code ?? 'http_error', first?.message ?? (xhr.statusText || 'Upload failed')))
      }
    }
    xhr.onerror = () => reject(new ApiError(0, 'network', 'Network error'))
    xhr.onabort = () => reject(new ApiError(0, 'aborted', 'Upload aborted'))
    const fd = new FormData()
    fd.append('file', file, file.name)
    xhr.send(fd)
  })
}

